import { MenusInterface } from "@/interface/app/ooc";
import moment from "moment-timezone";
import { MenusType, OOCCategoryType } from "@/types/app/ooc/menus.type";
import { Item } from "@/modules/location";
import { MENU_STEPPER } from "@/enums/ooc/ooc-menu.enum";
import Category from "@/modules/ooc/category";
import { Translate as $t } from "@/plugins/translate";

export default class Menu implements MenusInterface {
  name: string = "";
  id: string = "";
  locationId: string = "";
  updateTime: string = "";
  createTime: string = "";
  availableIntegrations: Array<any> = [];
  availableItems: number = 0;
  categories: Array<any> = [];
  external_id: string = "";
  items: Array<any> = [];
  step: number = MENU_STEPPER.CREATE;
  stagedCategories: Array<any> = [];
  stagedItems: Array<any> = [];
  activeIntegrations: number = 0;
  categoryIds: Array<string> = [];
  validations: any = {
    name: null,
    stagedCategories: null,
    key: 0,
  };

  public static MODULE_NAME: string = "ooc/menus";

  constructor(data?: MenusType) {
    if (data) {
      this.id = data.id;
      this.locationId = data.location_id;
      this.name = data.name || "";
      this.availableIntegrations = data.available_integrations;
      this.availableItems = data.available_items;
      this.categories = data.payload || [];
      this.external_id = data.external_id;
      this.items = this.prepareItems();
      this.step = MENU_STEPPER.CREATE;
      this.stagedCategories = this.prepareCategories();
      this.stagedItems = this.prepareStagedItems();
      this.createTime = data.created_at;
      this.updateTime = data.updated_at;
      this.activeIntegrations = 0;
      this.calculateActiveIntegrations();
    }
  }

  /**
   * time formatter
   * @return {string}
   */
  public formatTime(datetime): string {
    const formattedTime = moment(datetime);
    if (!formattedTime.isValid()) {
      return "";
    }
    return formattedTime?.utc(true)?.fromNow();
  }

  /**
   * get the items from all the categories and add them in one array
   * @return{array}
   */
  private prepareItems(): Array<any> {
    if (this.categories) {
      let items: Array<any> = [];
      this.categories?.forEach((category) => {
        items = [...items, ...category.items];
      });
      return this.stageMenuItems(items);
    }
    return [];
  }

  /**
   * prepare categories
   * @private
   */
  private prepareCategories(): Array<any> {
    if (this.categories) {
      return this.categories?.map((category) => {
        return new Category({ ...category, isStaged: true });
      });
    }

    return [];
  }
  /**
   * get the items from all the categories and add them in one array
   * @return{array}
   */
  public prepareStagedItems(): Array<any> {
    if (this.stagedCategories) {
      let items: Array<any> = [];
      this.stagedCategories?.forEach((category) => {
        items = [...items, ...category.items];
        items.forEach((item) => {
          if (!item.category_id) {
            item.category_id = category.id;
          }
        });
      });
      return this.stageMenuItems(items);
    }
    return [];
  }

  /**
   * will instantiate item object from the items array and set them as staged.
   * @return{array}
   */
  private stageMenuItems(items) {
    return items.map((item) => {
      let tempItem = new Item(item);
      tempItem.prepareOocItem();
      tempItem.isStaged = true;
      return tempItem;
    });
  }

  /***
   * a setter for step that checks if the name is valid before allowing to change step.
   * @param nextStep
   */
  public setStep(nextStep: number = MENU_STEPPER.CREATE): any {
    if (!this.validateStep(nextStep)) {
      return false;
    }
    this.step = nextStep;
  }

  /***
   * will validate before moving into other steps
   * @param nextStep
   */
  public validateStep(nextStep): boolean {
    if (nextStep === MENU_STEPPER.OWNER_MENU) {
      return this.validateStagedMenu();
    }
    return this.validateName();
  }

  /**
   * makes sure the name is valid
   * @private
   */
  private validateName(): boolean {
    const isValid = !!this.name;
    this.validations.name = !isValid ? $t("fill_all_required_fields_message") : null;
    this.validations.key++;
    return isValid;
  }

  /**
   * makes sure we have at least one staged category
   * @private
   */
  private validateStagedMenu() {
    const isValid = this.stagedCategories.length > 0;
    this.validations.stagedCategories = !isValid ? "no staged items" : null;
    this.validations.key++;
    return isValid;
  }

  /**
   * add the category to staged
   * @param category
   * @private
   */
  private addToStaged(category) {
    this.stagedCategories.push(category);
  }

  /**
   * removed the category from staged categories
   * @param category
   * @private
   */
  public removeFromStaged(category) {
    this.stagedCategories.splice(
      this.stagedCategories.findIndex((item) => item.name === category.name),
      1
    );
  }

  /**
   * remove items from staged items for this category
   * @param category
   */
  public removeStagedItemsForCategory(category) {
    this.stagedItems = this.stagedItems?.filter((item) => item.category_id !== category.id);
  }

  /**
   * will remove/add the category depending if it is used or not
   * @param category
   */
  public handlingTogglingCategory(category) {
    if (category.checkIsStaged(this.stagedCategories)) {
      this.removeStagedItemsForCategory(category);
      this.removeFromStaged(category);
    } else {
      this.addToStaged(category);
    }
  }

  /**
   * stage all categories in the catalog
   * @param categoriesCatalog
   */
  public stageAll(categoriesCatalog: Array<Category>) {
    categoriesCatalog.forEach((category) => {
      !category.checkIsStaged(this.stagedCategories) ? this.addToStaged(category) : 0;
    });
  }

  public getCategoryIds(): Array<string> {
    return this.stagedCategories.map((category: OOCCategoryType) => {
      return category.id;
    });
  }

  /**
   * when item is being searched it it only shows categories that has items found
   * @param catalogItems
   */
  public getCategoriesThatContainItems(catalogItems) {
    return this.stagedCategories.filter((category) => {
      return !!catalogItems.find((item) => item.category_id === category.id);
    });
  }

  /**
   * calculate the number of items in the category that is has items when searching
   * @param catalogItems
   * @param displayedCategories
   */
  public calculateCountOfResultForEachItem(catalogItems, displayedCategories): Array<any> {
    displayedCategories.forEach((category) => {
      category.items_count = 0;
      catalogItems.forEach((item) => {
        if (item.category_id === category.id) {
          ++category.items_count;
        }
      });
    });
    return displayedCategories;
  }

  /**
   * Checks if the menu is active and return how many are active and get the number
   * @private
   */
  public calculateActiveIntegrations(): void {
    this.activeIntegrations = this.availableIntegrations?.filter((integration) => {
      return integration.published;
    }).length;
  }

  /**
   *  checks the previous active one and switch off when faking active integration
   * @param stagedIntegration
   */
  public toggleActiveIntegrationStatusToFalse(stagedIntegration) {
    this.availableIntegrations.forEach((integration) => {
      if (integration.integration.id === stagedIntegration) {
        if (integration.published === true) {
          integration.published = false;
        }
      }
      this.calculateActiveIntegrations();
    });
  }

  /**
   *  turns the integration active for the item
   * @param stagedIntegration
   * @param item
   */
  public toggleIntegrationStatusToActive(stagedIntegration, item, integrationsAvailable) {
    if (item.id !== this.id) {
      return;
    }
    if (this.availableIntegrations.length !== integrationsAvailable.length) {
      this.availableIntegrations = integrationsAvailable;
    }
    this.availableIntegrations.forEach((integration) => {
      if (integration.integration.id === stagedIntegration) {
        integration.published = true;
      }
    });
    this.calculateActiveIntegrations();
  }

  /**
   * gets you the names of the active integrations as a string
   */
  public getActiveIntegrationNames(): string {
    return (
      this.availableIntegrations
        .filter((integration) => integration?.published)
        .map((integration) => integration?.integration?.integration_name)
        .join(",") || ``
    );
  }
}
